// Variant C: the fictional units we follow through the story.
// The units come from src/v2/heroUnits.ts (heroUnits plus rangeUnits);
// this file adds the C-only detail the story needs (plate rows, dates, documents, wording).
// Every brand, partner, owner, serial and date here is invented for illustration. None is a customer.
import { allUnits, type HeroUnit } from '../../heroUnits';
import { intelligence } from '../../content';

export type SectorKey = HeroUnit['key'];

export type Sector = {
  key: SectorKey;
  chip: string; // switcher label
  chipShort: string; // switcher label on phones
  noun: string; // "Follow one ___."
  mark: string; // the maker's monogram, centre of its QR
  maker: string;
  model: string; // without the maker, e.g. "AW-9"
  fullModel: string; // "Halvard AW-9"
  kind: string;
  serial: string;
  accent: string; // the maker's own brand colour inside their pages (white-label)
  accentInk: string;
  scene: string; // in-use photo (hero)
  scenePos: string; // object-position so the product sits above the plate
  sceneAlt: string;
  sceneFallback: string; // used until the generated unit photos exist
  product: string; // catalogue shot (thumbnails)
  plate: [string, string][];
  partner: string;
  partnerShort: string;
  partnerRole: HeroUnit['partnerRole'];
  handoverPlace: string;
  where: string; // where the unit works
  owner1: string;
  owner1Short: string;
  owner2: string;
  operator: string; // who the bulletin should reach
  dates: { built: string; shipped: string; handover: string; owned: string; asked: string; claim: string; bulletin: string; sold: string; recycled: string };
  wo: string;
  slip: string;
  cert: string;
  handoverName: string;
  checks: string[];
  manual: string;
  part: string;
  ask: { q: string; a: string; src: string; when: string };
  claim: { id: string; fault: string; evidence: string[] };
  bulletin: { code: string; title: string; action: string };
  service: { name: string; kit: string; date: string }; // the routine visit that keeps it working
  transferNote: string;
  passport: string[];
  materials: string[];
  takeBack: string;
};

type Extra = Omit<
  Sector,
  'key' | 'maker' | 'fullModel' | 'kind' | 'serial' | 'accent' | 'scene' | 'sceneAlt' | 'scenePos' | 'product' | 'partner' | 'partnerRole' | 'handoverName' | 'checks' | 'part' | 'ask'
> & { ask: { q: string; when: string; a?: string; src?: string } };

const EXTRA: Record<SectorKey, Extra> = {
  heatpump: {
    chip: 'Heat pump',
    chipShort: 'Heat pump',
    noun: 'heat pump',
    mark: 'K',
    model: 'AW-9',
    accentInk: '#FFFFFF',
    sceneFallback: '/images/v2/gen/heatpump-commission-b.jpg',
    plate: [
      ['Type', 'Air to water heat pump'],
      ['Model', 'AW-9'],
      ['Serial no.', 'KAW9-26-04182'],
      ['Year', '2026'],
      ['Refrigerant', 'R290'],
    ],
    partnerShort: 'Brightwell',
    handoverPlace: 'At 14 Orchard Lane',
    where: 'At home',
    owner1: 'H. Price, 14 Orchard Lane',
    owner1Short: 'H. Price',
    owner2: 'J. and S. Mehta',
    operator: 'the homeowner',
    dates: { built: '9 Feb 2026', shipped: '17 Feb 2026', handover: '12 Mar 2026', owned: '30 Mar 2026', asked: '14 Jan 2027', claim: '19 Jan 2027', bulletin: '8 Mar 2027', sold: '30 Jun 2030', recycled: '2042' },
    wo: 'WO-26-2290',
    slip: 'PS-26-08812',
    cert: 'Declaration of conformity',
    manual: 'Installation manual',
    ask: { q: intelligence.sample.q, a: intelligence.sample.a, src: intelligence.sample.source, when: 'One cold morning' },
    claim: { id: 'WC-3107', fault: 'Fan motor stalls again in frost', evidence: ['2 photos', 'Commissioning record', 'The owner’s chat'] },
    bulletin: { code: 'TB-07', title: 'Outdoor sensor connector: check and reseat', action: 'Visit booked with Brightwell Heating' },
    service: { name: 'annual service', kit: 'new filter and inhibitor', date: '12 Mar 2027' },
    transferNote: 'The house is sold. The heat pump, its warranty and its service record pass to the new owners.',
    passport: ['Energy label and EPREL entry', 'Refrigerant and materials', 'Spare parts and repair guides', 'Declaration of conformity'],
    materials: ['Refrigerant R290: recover first', 'Steel casing and copper coil', 'Controller board: WEEE route'],
    takeBack: 'Brightwell Heating',
  },
  mower: {
    chip: 'Ride-on',
    chipShort: 'Ride-on',
    noun: 'ride-on mower',
    mark: 'M',
    model: 'RM-42',
    accentInk: '#FFFFFF',
    sceneFallback: '/images/v2/gen/dealer-counter.jpg',
    plate: [
      ['Type', 'Ride-on lawn mower'],
      ['Model', 'RM-42'],
      ['Serial no.', 'MRM42-26-03317'],
      ['Year', '2026'],
      ['Engine', '452 cc petrol'],
    ],
    partnerShort: 'Fenwick',
    handoverPlace: 'At Ashcombe House',
    where: 'On the lawn',
    owner1: 'Ashcombe House',
    owner1Short: 'The owner',
    owner2: 'P. and D. Lowry',
    operator: 'the owner',
    dates: { built: '12 Jan 2026', shipped: '23 Jan 2026', handover: '6 Mar 2026', owned: '6 Mar 2026', asked: '28 Mar 2027', claim: '14 Apr 2027', bulletin: '2 Jun 2027', sold: '9 Apr 2031', recycled: '2038' },
    wo: 'WO-26-0418',
    slip: 'PS-26-02264',
    cert: 'Declaration of conformity',
    manual: "Owner's manual",
    ask: { q: 'It will not start on the first cut of spring. What do I check?', when: 'The first cut of spring' },
    claim: { id: 'WC-2731', fault: 'Deck drive belt slips under load', evidence: ['Short video', 'Pre-delivery inspection', 'Service history'] },
    bulletin: { code: 'SB-04', title: 'Blade bolt: check torque at the season service', action: 'Done at the season service with Fenwick' },
    service: { name: 'season service', kit: 'new blades and drive belt', date: '2 Jun 2027' },
    transferNote: 'The mower is sold on through Fenwick, with its remaining warranty and a full service history.',
    passport: ['Digital instructions (Machinery Regulation, from 20 Jan 2027)', 'Declaration of conformity', 'Spare parts, blades and service kits', 'Noise and emissions data'],
    materials: ['Steel deck and chassis', 'Engine: drain oil and fuel first', 'Lead-acid starter battery: separate route'],
    takeBack: 'Fenwick Garden Machinery',
  },
  oven: {
    chip: 'Oven',
    chipShort: 'Oven',
    noun: 'combi oven',
    mark: 'F',
    model: 'CX-10',
    accentInk: '#FFFFFF',
    sceneFallback: '/images/v2/gen/kitchen-service-b.jpg',
    plate: [
      ['Type', 'Combi steam oven'],
      ['Model', 'CX-10'],
      ['Serial no.', 'FCX10-26-00217'],
      ['Year', '2026'],
      ['Supply', '400 V three-phase, water feed'],
    ],
    partnerShort: 'Northline',
    handoverPlace: 'At Harbour Street Kitchen',
    where: 'In the kitchen',
    owner1: 'Harbour Street Kitchen',
    owner1Short: 'The head chef',
    owner2: 'Kiln Lane Brasserie',
    operator: 'the head chef',
    dates: { built: '2 Mar 2026', shipped: '12 Mar 2026', handover: '8 Apr 2026', owned: '9 Apr 2026', asked: '22 Oct 2026', claim: '6 Nov 2026', bulletin: '18 Feb 2027', sold: '11 Mar 2033', recycled: '2044' },
    wo: 'WO-26-1452',
    slip: 'PS-26-05530',
    cert: 'Electrical safety certificate',
    manual: 'Service manual',
    ask: { q: 'Steam is weaker than usual. What should we check?', when: 'Before lunch service' },
    claim: { id: 'WC-1950', fault: 'Steam generator element failed', evidence: ['3 photos', 'Installation sign-off', 'Descale log'] },
    bulletin: { code: 'SB-11', title: 'Door hinge bolt: check torque', action: 'Visit booked with Northline' },
    service: { name: 'six-month service', kit: 'door seal and descaler', date: '8 Apr 2027' },
    transferNote: 'The restaurant changes hands. The oven, its service log and its gas checks go with it.',
    passport: ['Materials and recycled content', 'Spare parts and repair guides', 'Electrical safety certificate', 'Dismantling at end of life'],
    materials: ['Stainless steel body', 'Glass door and insulation', 'Burners and controls: separate route'],
    takeBack: 'Northline Catering Services',
  },
  ebike: {
    chip: 'E-bike',
    chipShort: 'E-bike',
    noun: 'e-bike',
    mark: 'V',
    model: 'T8',
    accentInk: '#FFFFFF',
    sceneFallback: '/images/v2/gen/dealer-counter.jpg',
    plate: [
      ['Type', 'Electric step-through bike'],
      ['Model', 'T8'],
      ['Frame no.', 'VT8-26-18842'],
      ['Year', '2026'],
      ['Battery', '36 V, 625 Wh'],
    ],
    partnerShort: 'Canal Street',
    handoverPlace: 'At Canal Street Cycles',
    where: 'Around Leith',
    owner1: 'Maya, Leith',
    owner1Short: 'Maya',
    owner2: 'Sam, Portobello',
    operator: 'the rider',
    dates: { built: '16 Feb 2026', shipped: '27 Feb 2026', handover: '21 Mar 2026', owned: '21 Mar 2026', asked: '9 Nov 2027', claim: '23 Nov 2027', bulletin: '18 Feb 2028', sold: '4 May 2030', recycled: '2036' },
    wo: 'WO-26-0733',
    slip: 'PS-26-03981',
    cert: 'Declaration of conformity',
    manual: 'Battery manual',
    ask: { q: 'The range has dropped a lot this year. Is the battery failing?', when: 'Year two, a cold morning' },
    claim: { id: 'WC-3384', fault: 'Battery health check shows one cell group out of balance', evidence: ['Battery health report', 'Charge history', 'The rider’s chat'] },
    bulletin: { code: 'FW-05', title: 'Battery firmware: improved cold-weather range', action: 'Updated at the next visit to Canal Street' },
    service: { name: 'annual service', kit: 'brake pads and chain', date: '18 Feb 2028' },
    transferNote: 'Maya sells the bike. It goes with its frame number, its warranty and a battery history the second owner can trust.',
    passport: ['Battery passport (LMT batteries, from 18 Feb 2027)', 'Battery health and charge history', 'Spare parts and repair guides', 'Declaration of conformity'],
    materials: ['Lithium-ion battery: battery recycling route', 'Aluminium frame', 'Motor and electronics: WEEE route'],
    takeBack: 'Canal Street Cycles',
  },
  lift: {
    chip: 'Scissor lift',
    chipShort: 'Lift',
    noun: 'scissor lift',
    mark: 'T',
    model: 'TX12',
    accentInk: '#FFFFFF',
    sceneFallback: '/images/v2/gen/unit-scissor-lift.jpg',
    plate: [
      ['Type', 'Scissor lift, MEWP'],
      ['Model', 'TX12'],
      ['Serial no.', 'TB-TX12-2023-1186'],
      ['Year', '2023'],
      ['Safe working load', '450 kg, 2 persons'],
    ],
    partnerShort: 'Pennine',
    handoverPlace: 'At the Northgate Hire depot',
    where: 'On hire',
    owner1: 'Northgate Hire, Leeds depot',
    owner1Short: 'Northgate Hire',
    owner2: 'Calder Access Hire',
    operator: 'the operator',
    dates: { built: '21 Nov 2023', shipped: '1 Dec 2023', handover: '14 Mar 2024', owned: '14 Mar 2024', asked: '9 Oct 2024', claim: '23 Oct 2024', bulletin: '3 Feb 2025', sold: '12 Jun 2029', recycled: '2036' },
    wo: 'WO-23-2841',
    slip: 'PS-23-09127',
    cert: 'Declaration of conformity',
    manual: 'Operator manual',
    ask: { q: 'The platform has stopped at height. How do I bring it down?', when: 'On a hire job' },
    claim: { id: 'WC-1642', fault: 'Pothole guard not deploying', evidence: ['2 photos', 'Pre-use check log', 'Pre-delivery inspection'] },
    bulletin: { code: 'SB-09', title: 'Platform gate latch: check the spring', action: 'Done at the next examination with Pennine' },
    service: { name: 'LOLER thorough examination', kit: 'hydraulic filter and hose set', date: '14 Mar 2025' },
    transferNote: 'Northgate Hire sells the lift from its fleet. It goes with its serial, its examination reports and its service history.',
    passport: ['Digital instructions (Machinery Regulation, from 20 Jan 2027)', 'Thorough examination reports', 'Spare parts and repair guides', 'Declaration of conformity'],
    materials: ['Steel chassis and scissor pack', 'Hydraulic oil: drain first', 'Traction batteries: separate route'],
    takeBack: 'Pennine Lift Services',
  },
  battery: {
    chip: 'Battery',
    chipShort: 'Battery',
    noun: 'home battery',
    mark: 'A',
    model: 'H13',
    accentInk: '#FFFFFF',
    sceneFallback: '/images/v2/gen/unit-homebattery.jpg',
    plate: [
      ['Type', 'Home battery'],
      ['Model', 'H13'],
      ['Serial no.', 'AH13-26-07514'],
      ['Year', '2026'],
      ['Capacity', '13.5 kWh'],
    ],
    partnerShort: 'Northgate',
    handoverPlace: 'At 27 Mill Road',
    where: 'At home',
    owner1: 'R. Okafor, 27 Mill Road',
    owner1Short: 'R. Okafor',
    owner2: 'T. and L. Byrne',
    operator: 'the homeowner',
    dates: { built: '19 Feb 2026', shipped: '2 Mar 2026', handover: '14 Apr 2026', owned: '14 Apr 2026', asked: '3 Dec 2026', claim: '15 Jan 2027', bulletin: '10 Mar 2027', sold: '18 Sep 2031', recycled: '2041' },
    wo: 'WO-26-1187',
    slip: 'PS-26-04420',
    cert: 'Declaration of conformity',
    manual: 'Owner guide',
    ask: {
      q: 'The power went off in the storm and the battery did not take over. Why?',
      a: 'Backup only runs when a reserve is set, and yours is at 0%. Set a 20% reserve in Settings, then test it with the switch on the gateway.',
      src: 'Owner guide p.14',
      when: 'During a winter storm',
    },
    claim: { id: 'WC-4120', fault: 'Backup gateway relay fails its test', evidence: ['Event log', 'Commissioning record', 'The owner’s chat'] },
    bulletin: { code: 'FW-12', title: 'Firmware: faster switch to backup', action: 'Updated overnight, over the air' },
    service: { name: 'annual health check', kit: 'new cooling fan filter', date: '14 Apr 2027' },
    transferNote: 'The house is sold. The battery, its warranty and its charge history pass to the new owners.',
    passport: ['Battery passport (from 18 Feb 2027)', 'State of health and charge history', 'Carbon footprint and recycled content', 'Spare parts and repair guides'],
    materials: ['Lithium iron phosphate cells: battery recycling route', 'Aluminium enclosure', 'Inverter and electronics: WEEE route'],
    takeBack: 'Northgate Solar',
  },
  robotmower: {
    chip: 'Robot mower',
    chipShort: 'Robot',
    noun: 'robot mower',
    mark: 'V',
    model: 'R7',
    accentInk: '#FFFFFF',
    sceneFallback: '/images/v2/gen/unit-robotmower.jpg',
    plate: [
      ['Type', 'Robot lawn mower'],
      ['Model', 'R7'],
      ['Serial no.', 'VR7-26-22609'],
      ['Year', '2026'],
      ['Lawn', 'Up to 1,500 m²'],
    ],
    partnerShort: 'Hollins',
    handoverPlace: 'At 6 Beech Close',
    where: 'In the garden',
    owner1: 'A. Novak, 6 Beech Close',
    owner1Short: 'A. Novak',
    owner2: 'C. and M. Reid',
    operator: 'the owner',
    dates: { built: '9 Mar 2026', shipped: '18 Mar 2026', handover: '22 Apr 2026', owned: '22 Apr 2026', asked: '6 Jun 2026', claim: '19 Aug 2026', bulletin: '2 Sep 2026', sold: '12 May 2030', recycled: '2035' },
    wo: 'WO-26-1530',
    slip: 'PS-26-05902',
    cert: 'Declaration of conformity',
    manual: "Owner's guide",
    ask: {
      q: 'It keeps stopping by the hedge with a lost signal message. What do I do?',
      a: 'The tall hedge blocks the satellite signal. Move the boundary 50 cm away from it in the lawn map, or turn on edge assist for that zone.',
      src: "Owner's guide p.17",
      when: 'Early summer',
    },
    claim: { id: 'WC-2286', fault: 'Drive motor stalls on the slope', evidence: ['Short video', 'Installation record', 'Error log'] },
    bulletin: { code: 'FW-08', title: 'Firmware: better grip on wet slopes', action: 'Updated overnight, over the air' },
    service: { name: 'winter service', kit: 'new blades and skid plate', date: '4 Nov 2026' },
    transferNote: 'The house is sold. The mower stays with the garden, with its warranty and its lawn map.',
    passport: ['Digital instructions (Machinery Regulation, from 20 Jan 2027)', 'Battery data and charge history', 'Spare parts and repair guides', 'Declaration of conformity'],
    materials: ['Lithium-ion battery: battery recycling route', 'Recyclable plastic body', 'Motors and electronics: WEEE route'],
    takeBack: 'Hollins Garden Centre',
  },
  icemaker: {
    chip: 'Ice',
    chipShort: 'Ice',
    noun: 'ice machine',
    mark: 'N',
    model: 'IM-150',
    accentInk: '#FFFFFF',
    sceneFallback: '/images/v2/gen/unit-icemaker.jpg',
    plate: [
      ['Type', 'Ice machine, self-contained'],
      ['Model', 'IM-150'],
      ['Serial no.', 'NIM150-26-01488'],
      ['Year', '2026'],
      ['Output', '150 kg a day'],
    ],
    partnerShort: 'Coldwell',
    handoverPlace: 'At The Copper Room',
    where: 'Behind the bar',
    owner1: 'The Copper Room',
    owner1Short: 'The bar manager',
    owner2: 'Lantern Yard Bar',
    operator: 'the bar manager',
    dates: { built: '23 Feb 2026', shipped: '4 Mar 2026', handover: '9 Apr 2026', owned: '9 Apr 2026', asked: '13 Nov 2026', claim: '27 Nov 2026', bulletin: '15 Jan 2027', sold: '3 Mar 2032', recycled: '2038' },
    wo: 'WO-26-1294',
    slip: 'PS-26-04711',
    cert: 'Electrical safety certificate',
    manual: 'Service manual',
    ask: {
      q: 'The ice is coming out cloudy and small tonight. What should I check?',
      a: 'Usually a spent water filter or scale. Yours was changed seven months ago, past the six-month interval. Change the filter and run a clean cycle.',
      src: 'Service manual p.22',
      when: 'Friday, before opening',
    },
    claim: { id: 'WC-1873', fault: 'Water pump failed mid-harvest', evidence: ['2 photos', 'Installation sign-off', 'Cleaning log'] },
    bulletin: { code: 'SB-03', title: 'Inlet valve: check for slow fill', action: 'Visit booked with Coldwell' },
    service: { name: 'six-month clean', kit: 'new water filter and sanitiser', date: '9 Feb 2027' },
    transferNote: 'The bar changes hands. The ice machine, its cleaning log and its warranty go with it.',
    passport: ['Refrigerant and F-gas data', 'Spare parts and repair guides', 'Cleaning and hygiene log', 'Declaration of conformity'],
    materials: ['Refrigerant: recover first', 'Stainless steel body and bin', 'Compressor and controls: WEEE route'],
    takeBack: 'Coldwell Refrigeration',
  },
  coffee: {
    chip: 'Coffee',
    chipShort: 'Coffee',
    noun: 'coffee machine',
    mark: 'C',
    model: 'E2',
    accentInk: '#FFFFFF',
    sceneFallback: '/images/v2/gen/unit-coffee.jpg',
    plate: [
      ['Type', 'Espresso machine, two group'],
      ['Model', 'E2'],
      ['Serial no.', 'CE2-26-05531'],
      ['Year', '2026'],
      ['Supply', '230 V, water feed'],
    ],
    partnerShort: 'Tamperline',
    handoverPlace: 'At Tidewater Café',
    where: 'On the counter',
    owner1: 'Tidewater Café',
    owner1Short: 'The café owner',
    owner2: 'Harbour Lights Café',
    operator: 'the barista',
    dates: { built: '5 Mar 2026', shipped: '13 Mar 2026', handover: '16 Apr 2026', owned: '16 Apr 2026', asked: '19 Sep 2026', claim: '6 Dec 2026', bulletin: '20 Jan 2027', sold: '2 Aug 2031', recycled: '2039' },
    wo: 'WO-26-1611',
    slip: 'PS-26-06134',
    cert: 'Pressure equipment certificate',
    manual: 'Service manual',
    ask: {
      q: 'Shots are running fast and tasting sour this morning. Is it the machine?',
      a: 'Probably the grind. Go two steps finer and check the brew pressure reads 9 bar. Yours reads 9.1, so the machine is fine.',
      src: 'Barista guide p.8',
      when: 'Saturday, the first rush',
    },
    claim: { id: 'WC-2045', fault: 'Boiler pressure switch failed', evidence: ['2 photos', 'Installation sign-off', 'Cleaning log'] },
    bulletin: { code: 'SB-06', title: 'Steam valve: replace the seal', action: 'Visit booked with Tamperline' },
    service: { name: 'six-month service', kit: 'new group gaskets and water filter', date: '16 Feb 2027' },
    transferNote: 'The café changes hands. The machine, its service log and its warranty go with it.',
    passport: ['Energy use and standby', 'Spare parts and repair guides', 'Pressure equipment certificate', 'Declaration of conformity'],
    materials: ['Stainless steel and brass', 'Copper boiler', 'Pump and controls: WEEE route'],
    takeBack: 'Tamperline Coffee Services',
  },
  hottub: {
    chip: 'Hot tub',
    chipShort: 'Hot tub',
    noun: 'hot tub',
    mark: 'C',
    model: 'S6',
    accentInk: '#FFFFFF',
    sceneFallback: '/images/v2/gen/unit-hottub.jpg',
    plate: [
      ['Type', 'Hot tub, six seats'],
      ['Model', 'S6'],
      ['Serial no.', 'CS6-26-03075'],
      ['Year', '2026'],
      ['Supply', '230 V, 32 A'],
    ],
    partnerShort: 'Westbrook',
    handoverPlace: 'At The Old Barn',
    where: 'In the garden',
    owner1: 'K. Walsh, The Old Barn',
    owner1Short: 'K. Walsh',
    owner2: 'The Hendersons',
    operator: 'the owner',
    dates: { built: '2 Apr 2026', shipped: '14 Apr 2026', handover: '8 May 2026', owned: '8 May 2026', asked: '27 Jul 2026', claim: '18 Dec 2026', bulletin: '12 Jan 2027', sold: '21 Jun 2032', recycled: '2040' },
    wo: 'WO-26-1802',
    slip: 'PS-26-07245',
    cert: 'Electrical safety certificate',
    manual: "Owner's guide",
    ask: {
      q: 'The water has gone cloudy after the weekend. What do I do?',
      a: 'Normal after heavy use. Shock the water, rinse both filters and keep chlorine between 3 and 5 ppm. Your filters are due for a change in two weeks.',
      src: "Owner's guide p.21",
      when: 'The Monday after a party',
    },
    claim: { id: 'WC-2512', fault: 'Heater shows FL1 on a cold night', evidence: ['Short video', 'Handover record', 'The owner’s chat'] },
    bulletin: { code: 'FW-03', title: 'Control panel: better frost protection', action: 'Updated overnight, over the air' },
    service: { name: 'annual service', kit: 'new filters and cover clips', date: '8 May 2027' },
    transferNote: 'The house is sold. The hot tub stays, with its warranty and its service history.',
    passport: ['Energy use and standby heat loss', 'Spare parts and repair guides', 'Electrical safety record', 'Declaration of conformity'],
    materials: ['Acrylic shell', 'Wood-effect cladding', 'Pumps, heater and controls: WEEE route'],
    takeBack: 'Westbrook Spas',
  },
};

// Hero crops: the plate covers the lower 40% of the stage, so each photo must show its product in the top half.
const HERO_SCENE: Record<HeroUnit['key'], { scene: string; sceneAlt: string; scenePos: string }> = {
  heatpump: { scene: '/images/v2/gen/heatpump-commission.jpg', sceneAlt: 'An installer commissioning an air source heat pump outside a brick house', scenePos: '88% 50%' },
  mower: { scene: '/images/v2/gen/mower-hero.jpg', sceneAlt: 'A woman mowing a large lawn at a country house on a green ride-on mower on a spring morning', scenePos: '36% 18%' },
  oven: { scene: '/images/v2/gen/kitchen-combi.jpg', sceneAlt: 'A chef sliding a tray into a stainless steel combi oven in a busy restaurant kitchen', scenePos: '62% 50%' },
  ebike: { scene: '/images/v2/gen/ebike-hero.jpg', sceneAlt: 'A woman riding a slate blue step-through e-bike along a city bike lane in morning light', scenePos: '54% 22%' },
  lift: { scene: '/images/v2/gen/lifting-inspection.jpg', sceneAlt: 'An engineer in a hi-vis vest inspecting a scissor lift in a bright warehouse', scenePos: '70% 40%' },
  battery: { scene: '/images/v2/gen/scene-homebattery.jpg', sceneAlt: 'A woman in her garage at home checking her phone beside a white home battery', scenePos: '70% 40%' },
  robotmower: { scene: '/images/v2/gen/scene-robotmower.jpg', sceneAlt: 'A robot mower crossing a striped lawn while a man watches from the patio with a coffee', scenePos: '84% 50%' },
  icemaker: { scene: '/images/v2/gen/scene-icemaker.jpg', sceneAlt: 'A bartender scooping ice from a stainless steel ice machine behind a busy bar', scenePos: '65% 40%' },
  coffee: { scene: '/images/v2/gen/scene-coffee.jpg', sceneAlt: 'A barista locking a portafilter into a stainless steel espresso machine in a bright café', scenePos: '65% 40%' },
  hottub: { scene: '/images/v2/gen/scene-hottub.jpg', sceneAlt: 'A couple relaxing in a hot tub on a garden deck at dusk, steam rising', scenePos: '78% 50%' },
};

const toSector = (u: HeroUnit): Sector => {
  const x = EXTRA[u.key];
  return {
    ...x,
    key: u.key,
    maker: u.brand,
    fullModel: u.model,
    kind: u.kind,
    serial: u.serial,
    accent: u.accent,
    ...HERO_SCENE[u.key],
    product: u.product,
    partner: u.partner,
    partnerRole: u.partnerRole,
    handoverName: u.handoverName,
    checks: u.checks,
    part: u.part,
    ask: { q: x.ask.q, when: x.ask.when, a: x.ask.a ?? u.answer, src: x.ask.src ?? u.source },
  };
};

/** The homepage range, in hero order. */
// Machines lead the hero (sold through partners, serviced, resold), then the wider range.
// The scissor lift leads; one mower only (the ride-on), so the robot mower stays off the homepage.
// Six chips at most: the ice machine stays off the homepage so the chips fit on a phone.
const HOME: SectorKey[] = ['lift', 'mower', 'ebike', 'battery', 'coffee', 'hottub'];
const unitOf = (k: SectorKey) => allUnits.find((u) => u.key === k)!;
export const SECTORS: Sector[] = HOME.map(unitOf).map(toSector);
/** Every unit, including the worked example (Halvard heat pump and friends) used on the tour and platform pages. */
export const ALL_SECTORS: Sector[] = [...SECTORS, ...allUnits.filter((u) => !HOME.includes(u.key)).map(toSector)];

export const sectorByKey = (k: SectorKey) => ALL_SECTORS.find((s) => s.key === k) ?? SECTORS[0];
export const sectorIndex = (k: SectorKey) => Math.max(0, SECTORS.findIndex((s) => s.key === k));
